import { useState, useContext } from "react";

import * as Sentry from "@sentry/react";

import { Grid, Typography, Avatar } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faMoneyCheck,
  faTag,
  faExclamation
} from "@fortawesome/free-solid-svg-icons";
import { faCalendarExclamation } from "@fortawesome/pro-solid-svg-icons";

import { LoadingButton } from "@aclymatepackages/atoms";
import { ucFirstLetters } from "@aclymatepackages/formatters";

import Card from "../../atoms/mui/Card";
import GrayPaper from "../../atoms/GrayPaper";

import { analyticsTrack } from "../../../helpers/analytics";

import { useAccountData, useAuth } from "../../../helpers/firebase";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { useCustomerCoupon } from "../../../helpers/hooks/stripe";

import {
  generateDisplayIcons,
  googlePurchaseTracking,
  fireConfetti
} from "../../../helpers/components/purchase";
import { useGoldTier } from "../../../helpers/components/badges";

import usePurchasingContext from "../../../helpers/contexts/purchasing";
import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";
import useEmissionsContext from "../../../helpers/contexts/emissions";

const PurchaseOffsetFooter = ({
  setTransactionId,
  paymentMethod,
  purchaseCompleted,
  setPurchaseCompleted
}) => {
  const { selectedPortfolio } = usePortfoliosContext();
  const {
    id: portfolioId,
    stripeOneTimePriceId,
    primary_image_url: portfolioImage,
    name: portfolioName
  } = selectedPortfolio || {};

  const {
    purchaseTons,
    purchaseDollars,
    nonDiamondYears,
    selectedScopeChips,
    selectedTab,
    selectedEvents,
    attributionYear
  } = usePurchasingContext();
  const [
    {
      stripeCustomerId: companyObjStripeCustomerId,
      billing,
      cnaughtSubaccountId,
      name: companyName
    }
  ] = useAccountData();
  const [{ email }] = useAuth();
  const { isEarned: isGoldEarned } = useGoldTier();

  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  const buildBadgeYears = () => {
    if (selectedTab !== "scopes") {
      return null;
    }

    const uniqueChipYears = [
      ...new Set(selectedScopeChips.map(({ year }) => year))
    ];

    return uniqueChipYears.map((year) => {
      const isDiamond = !!selectedScopeChips.find(
        (chip) => chip.year === year && chip.type === "diamond"
      );

      return { year, badge: isDiamond ? "diamond" : "platinum" };
    });
  };
  const badgeYears = buildBadgeYears();
  const formattedSelectedEvents =
    Array.isArray(selectedEvents) && selectedEvents.length
      ? selectedEvents.map(({ id, name }) => ({ id, name }))
      : null;

  const purchaseOffset = async () => {
    setPurchaseLoading(true);

    const accountId = window.sessionStorage.getItem("accountId");

    const { stripeCustomerId: billingObjStripeCustomerId } = billing || {};
    const { paymentMethodId, us_bank_account } = paymentMethod || {};

    const callAnalyticsTrackers = () => {
      if (badgeYears) {
        badgeYears.forEach(({ year, badge }) =>
          analyticsTrack(badge === "diamond" ? "Offset S3" : "Offset S1+2", {
            year
          })
        );
      }

      const earnedBadgesEventCalls = () => {
        if (!isGoldEarned || !(badgeYears.length === nonDiamondYears.length)) {
          return;
        }

        const isEveryBadgeYearDiamond = badgeYears.every(
          ({ badge }) => badge === "diamond"
        );

        if (isEveryBadgeYearDiamond) {
          return analyticsTrack("Diamond Unlock");
        }

        return analyticsTrack("Platinum Unlock");
      };

      earnedBadgesEventCalls();
      googlePurchaseTracking("add_shipping_info", {
        portfolio: selectedPortfolio,
        purchaseDollars,
        purchaseTons
      });
      return analyticsTrack("CNaught Portfolio Purchased", {
        portfolio: selectedPortfolio,
        tons: purchaseTons,
        price: purchaseDollars
      });
    };

    const handleFailure = () => {
      setPurchaseLoading(false);
      setPurchaseError(true);
    };

    try {
      const data = await new Promise((resolve, reject) => {
        fetchOurApi({
          path: "/stripe/one-time-portfolio-purchase",
          method: "POST",
          data: {
            accountId,
            purchaseTons,
            portfolioId,
            stripeOneTimePriceId,
            paymentMethodId,
            stripeCustomerId:
              companyObjStripeCustomerId || billingObjStripeCustomerId,
            badgeYears,
            events: formattedSelectedEvents,
            attributionYear,
            subaccountId: cnaughtSubaccountId,
            companyName,
            email,
            paymentType: us_bank_account ? "ach" : "card",
            portfolioImage,
            portfolioName
          },
          callback: resolve
        }).catch(reject);
      });

      if (!data || data.error) {
        Sentry.captureMessage("portfolio purchase returned error", {
          level: "error",
          tags: { feature: "offsets-purchase" },
          extra: { message: data?.message, portfolioId }
        });
        return handleFailure();
      }

      callAnalyticsTrackers();
      fireConfetti();

      setTransactionId(data.invoiceId);
      return setPurchaseCompleted(true);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "offsets-purchase" },
        extra: { portfolioId }
      });
      return handleFailure();
    }
  };

  return (
    <>
      {purchaseError && (
        <Grid item>
          <Typography variant="body2" color="error" align="center">
            We couldn&apos;t complete this purchase. Your card was not charged.
            Please try again or contact support@aclymate.com if the issue
            continues.
          </Typography>
        </Grid>
      )}
      <Grid
        item
        container
        justifyContent="center"
        spacing={2}
        alignItems="center"
      >
        <Grid item>
          <LoadingButton
            disabled={!purchaseDollars || purchaseCompleted}
            label="confirm this purchase"
            variant="contained"
            color="secondary"
            onClick={purchaseOffset}
            isLoading={purchaseLoading && !purchaseCompleted}
          />
        </Grid>
      </Grid>
    </>
  );
};

const PurchaseConfirmation = ({
  setTransactionId,
  setPurchaseCompleted,
  purchaseCompleted
}) => {
  const { currentCarbonBalanceTons } = useEmissionsContext();
  const { purchaseTons, purchaseDollars, selectedScopeChips, selectedTab } =
    usePurchasingContext();

  const { paymentMethod } = useContext(StripeCustomerContext);
  const { card, us_bank_account } = paymentMethod || {};
  const { last4: cardLast4, brand } = card || {};
  const { bank_name, last4: bankLast4 } = us_bank_account || {};

  const [offsetsCoupon] = useCustomerCoupon("offsets");
  const { percentOff, amountOff } = offsetsCoupon || {};

  const generateBadgeLevelYears = () => {
    if (selectedTab !== "scopes") {
      return {};
    }
    const diamondChips = selectedScopeChips.filter(
      ({ type }) => type === "diamond"
    );

    if (diamondChips.length) {
      return {
        badge: "diamond",
        years: diamondChips.length,
        badgeName: "Scope 3 Offsets"
      };
    }

    const platinumChips = selectedScopeChips.filter(
      ({ type }) => type === "platinum"
    );

    if (platinumChips.length) {
      return {
        badge: "platinum",
        years: platinumChips.length,
        badgeName: "Scope 1 + 2 Offsets"
      };
    }

    return {};
  };

  const { badge, years: badgeYears, badgeName } = generateBadgeLevelYears();

  const getDiscountPrice = () => {
    if (percentOff) {
      return purchaseDollars * ((100 - percentOff) / 100);
    }

    if (amountOff) {
      return purchaseDollars - amountOff;
    }

    return null;
  };

  const discountPrice = getDiscountPrice();

  const displayIcons = generateDisplayIcons({
    purchaseDollars,
    purchaseTons,
    discountPrice,
    badge,
    badgeYears,
    badgeName
  });

  const createPaymentDisclaimerObj = () => {
    if (us_bank_account) {
      return {
        icon: faMoneyCheck,
        text: `We will bill your ${ucFirstLetters(
          bank_name
        )} bank account with an account number ending in ${bankLast4} for this transaction. It may take up to four business days to bill your bank account.`
      };
    }

    return {
      icon: faCreditCard,
      text: `We will bill your ${ucFirstLetters(
        brand
      )} credit card ending in ${cardLast4} for this transaction.`
    };
  };

  const purchaseDisclaimers = offsetsCoupon
    ? [
        {
          icon: faTag,
          text: `A ${
            percentOff ? `${percentOff}%` : `$${amountOff}`
          } discount will be applied to your purchase.`
        }
      ]
    : [];

  const notFullyOffsetDisclaimer =
    currentCarbonBalanceTons < 0 &&
    Math.abs(currentCarbonBalanceTons) > purchaseTons
      ? [
          {
            icon: faCalendarExclamation,
            text: "This purchase will not fully offset your current carbon footprint and you will still have a negative carbon balance."
          }
        ]
      : [];

  const disclaimers = [
    createPaymentDisclaimerObj(),
    ...purchaseDisclaimers,
    ...notFullyOffsetDisclaimer,
    {
      icon: faExclamation,
      text: "All offset purchases are non-refundable since your offsets will be retired immediately."
    }
  ];

  return (
    <GrayPaper p={2}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h3">
            Please confirm your purchase of offsets
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            We need you to review the information and disclaimers about this
            purchase.
          </Typography>
        </Grid>
        <Grid item container spacing={1} alignItems="stretch">
          {displayIcons.map(({ icon, title, subtitle }, idx) => (
            <Card
              key={`purchase-display-icon-${idx}`}
              gridProps={{ sm: badge ? 4 : 6 }}
              style={{ height: "100%" }}
              content={
                <Grid
                  container
                  spacing={1}
                  direction="column"
                  alignItems="center"
                >
                  <Grid item>{icon}</Grid>
                  <Grid item>
                    <Typography variant="h5" align="center" color="textPrimary">
                      {title}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="subtitle2"
                      align="center"
                      color="textSecondary"
                    >
                      {subtitle}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          ))}
        </Grid>
        {disclaimers.map(({ avatar, icon, text }, idx) => (
          <Grid
            key={`purchase-disclaimer-${idx}`}
            item
            container
            spacing={2}
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Avatar>{icon ? <FontAwesomeIcon icon={icon} /> : avatar}</Avatar>
            </Grid>
            <Grid item>
              <Typography variant="body1">{text}</Typography>
            </Grid>
          </Grid>
        ))}
        <PurchaseOffsetFooter
          setTransactionId={setTransactionId}
          paymentMethod={paymentMethod}
          purchaseCompleted={purchaseCompleted}
          setPurchaseCompleted={setPurchaseCompleted}
        />
      </Grid>
    </GrayPaper>
  );
};
export default PurchaseConfirmation;
